<div class="header-div">
  <div class="header-content d-flex justify-content-between align-items-center">
    <div class="header-title-section">
      <h5 class="dialog-title mb-2">{{ 'Edit' | translate }} {{ data.sampleIds.length }} {{ 'Samples' | translate
      }}</h5>
      <p class="step-label small text-muted mb-0">
        @switch (currentStep) {
          @case ('picker') {
            <span>{{ 'Step 1: Choose fields to edit' | translate }}</span>
          }
          @case ('edit') {
            <span>{{ 'Step 2: Set values' | translate }}</span>
          }
          @case ('processing') {
            <span>{{ 'Processing your changes...' | translate }}</span>
          }
          @case ('result') {
            <span>{{ 'Changes applied' | translate }}</span>
          }
        }
      </p>
    </div>
    <div class="header-actions d-flex align-items-center gap-3">
      <span class="sample-count-badge">
        <mat-icon>checklist</mat-icon>
        <span>{{ data.sampleIds.length }} {{ 'samples' | translate }}</span>
      </span>
      <button class="btn close-icon p-0" (click)="closeDialog()" [disabled]="currentStep === 'processing'"
        [attr.aria-label]="'Close'">
        <mat-icon>close</mat-icon>
      </button>
    </div>
  </div>
</div>

<mat-dialog-content class="custom-scrollbar">
  <div>
    @switch (currentStep) {
      <!-- PICKER STEP -->
      @case ('picker') {
        <div class="step-container">
          <p class="step-description mb-3">
            {{ 'Select up to 3 fields to edit. You can repeat this process for additional fields.' | translate }}
          </p>
          <div class="search-wrapper mb-3">
            <mat-icon class="search-icon">search</mat-icon>
            <input type="text" class="form-control search-input" [(ngModel)]="pickerSearchTerm"
              (ngModelChange)="onPickerSearchTermChange($event)"
              placeholder="{{ 'Search fields...' | translate }}" autocomplete="off">
            </div>
            @if (selectedFieldIds.length > 0) {
              <div class="selected-fields-section mb-3">
                <label class="section-label">{{ 'Selected Fields' | translate }}</label>
                <div class="chips-container">
                  @for (selectedField of selectedFields; track trackByIndex($index)) {
                    <div class="chip">
                      <span>{{ selectedField.name | translate }}</span>
                      <button class="chip-remove" (click)="removeField(selectedField.id)" type="button"
                        [attr.aria-label]="'Remove field'">
                        <mat-icon>close</mat-icon>
                      </button>
                    </div>
                  }
                </div>
              </div>
            }
            @if (isAtFieldLimit) {
              <div class="alert alert-warning mb-3" role="alert">
                <mat-icon>info</mat-icon>
                <span class="ms-2">{{ 'Maximum 3 fields per edit. Apply these changes first, then edit more fields.' | translate }}</span>
              </div>
            }
            <div class="field-picker-list">
              @for (category of pickerCategories; track trackByIndex($index)) {
                <div class="field-category">
                  <div class="category-header">{{ category.name | translate }}</div>
                  <div class="fields-group">
                    @for (field of category.fields; track trackByIndex($index)) {
                      <div class="field-item"
                        (click)="!field.isDisabled && toggleField(field.id)"
                        [class.selected]="field.isSelected" [class.disabled]="field.isDisabled">
                        <input class="form-check-input" type="checkbox" [checked]="field.isSelected"
                          [disabled]="field.isDisabled" [id]="'field-' + field.id">
                          <label class="field-name" [for]="'field-' + field.id">{{ field.name | translate }}</label>
                          <span class="field-type">{{ field.typeLabel }}</span>
                        </div>
                      }
                    </div>
                  </div>
                }
                @if (pickerCategories.length === 0) {
                  <div class="text-center text-muted empty-state">
                    <mat-icon>search_off</mat-icon>
                    <p>{{ 'No fields match your search.' | translate }}</p>
                  </div>
                }
              </div>
            </div>
          }
          <!-- EDIT STEP -->
          @case ('edit') {
            <div class="step-container">
              <p class="edit-instruction mb-3 text-muted small">
                {{ 'Set values for the selected fields. These values will be applied to all {{count}} selected samples.' | translate: {count: data.sampleIds.length} }}
              </p>
              <div class="row">
                @for (field of editFields; track trackByIndex($index)) {
                  <div class="col-12">
                    <div class="mb-3">
                      <label class="form-label small fw-bold text-uppercase text-muted">
                        {{ field.name | translate }}
                      </label>
                      @switch (field.type) {
                        @case ('select') {
                          <select class="form-control form-control-sm" [(ngModel)]="editValues[field.id]"
                            >
                            <option [ngValue]="null" disabled>-- {{ 'Select' | translate }} --</option>
                            @for (opt of field.options || []; track trackByIndex($index)) {
                              <option
                                [ngValue]="opt.id">
                                {{ opt.name | translate }}
                              </option>
                            }
                          </select>
                        }
                        @case ('datetime') {
                          <div class="date-time-inputs">
                            <input class="form-control form-control-sm" type="date" [(ngModel)]="editValues[field.id]">
                            <input class="form-control form-control-sm" type="time" [(ngModel)]="editTimes[field.id]">
                          </div>
                        }
                        @case ('time') {
                          <input class="form-control form-control-sm" type="time" [(ngModel)]="editValues[field.id]">
                        }
                        @case ('number') {
                          <div>
                            <input class="form-control form-control-sm" type="number" step="any"
                              [(ngModel)]="editValues[field.id]" [placeholder]="field.placeholder || ''">
                              @if (field.unit) {
                                <small class="text-muted d-block mt-1">
                                  {{ 'Unit' | translate }}: {{ field.unit }}
                                </small>
                              }
                            </div>
                          }
                          @case ('textarea') {
                            <textarea class="form-control form-control-sm" [(ngModel)]="editValues[field.id]"
                            [placeholder]="field.placeholder || ''"></textarea>
                          }
                          @default {
                            <input class="form-control form-control-sm" type="text" [(ngModel)]="editValues[field.id]"
                              [placeholder]="field.placeholder || ''">
                          }
                        }
                      </div>
                    </div>
                  }
                </div>
              </div>
            }
            <!-- PROCESSING STEP -->
            @case ('processing') {
              <div class="step-container text-center py-5">
                <mat-spinner diameter="50" class="m-auto mb-3"></mat-spinner>
                <p class="text-muted small">
                  {{ 'Applying changes to {{count}} samples...' | translate: {count: data.sampleIds.length} }}
                </p>
                <div class="progress mb-2 progress-thin">
                  <div class="progress-bar" [style.width.%]="processing.progressPercent"></div>
                </div>
                <small class="text-muted">{{ processing.completed }} {{ 'of' | translate }} {{ processing.total
                }}</small>
              </div>
            }
            <!-- RESULT STEP -->
            @case ('result') {
              <div class="step-container">
                <div class="alert alert-success mb-2" role="alert">
                  <small class="fw-bold">
                    {{ '{{successCount}} of {{total}} samples updated successfully' | translate: {successCount: result.successCount, total: processing.total} }}
                  </small>
                </div>
                @if (result.failureCount > 0) {
                  <div class="alert alert-warning mb-2" role="alert">
                    <small class="fw-bold">
                      {{ '{{failureCount}} samples failed' | translate: {failureCount: result.failureCount} }}
                    </small>
                  </div>
                }
                <div class="mb-3">
                  <label class="small fw-bold text-uppercase text-muted d-block mb-2">{{ 'Fields Updated' | translate
                  }}:</label>
                  <div class="d-flex flex-wrap gap-2">
                    @for (change of result.changedFields; track trackByIndex($index)) {
                      <span class="badge badge-info"
                        >
                        <small>{{ change.name }}: {{ change.value }}</small>
                      </span>
                    }
                  </div>
                </div>
                @if (result.failureCount > 0) {
                  <div class="mb-3">
                    <label class="small fw-bold text-uppercase text-muted d-block mb-2">{{ 'Failed' | translate
                    }}:</label>
                    <div class="border rounded p-2 failed-list">
                      @for (failed of result.failedSamples; track trackByIndex($index)) {
                        <div class="d-flex justify-content-between mb-2"
                          >
                          <small class="fw-bold">{{ failed.id }}</small>
                          <small class="text-danger">{{ failed.error }}</small>
                        </div>
                      }
                    </div>
                  </div>
                }
                <div class="alert alert-info mb-0" role="alert">
                  <small>
                    {{ 'Need to change more fields?' | translate }}
                    <button type="button" (click)="editMoreFields()" class="alert-link">
                      {{ 'Edit more fields' | translate }}
                    </button>
                  </small>
                </div>
              </div>
            }
          }
        </div>
      </mat-dialog-content>

      <mat-dialog-actions class="border-top pt-3 pb-3 d-flex justify-content-between">
        <div class="left-btns d-flex gap-2">
          <button class="btn btn-outline-secondary btn-sm" (click)="closeDialog()"
            [disabled]="currentStep === 'processing'">
            {{ 'Cancel' | translate }}
          </button>
        </div>
        <div class="right-btns d-flex gap-2">
          @switch (currentStep) {
            @case ('picker') {
              <button class="btn btn-primary btn-sm" (click)="goToEditStep()" [disabled]="isPickerNextDisabled">
                {{ 'Next: Set Values' | translate }}
                <mat-icon class="ms-1 icon-button">arrow_forward</mat-icon>
              </button>
            }
            @case ('edit') {
              <button class="btn btn-outline-secondary btn-sm" (click)="goBack()">
                <mat-icon class="me-1 icon-button">arrow_back</mat-icon>
                {{ 'Back' | translate }}
              </button>
              <button class="btn btn-primary btn-sm" (click)="startApply()" [disabled]="isApplyDisabled">
                {{ 'Apply Changes' | translate }}
              </button>
            }
            @case ('result') {
              <button class="btn btn-outline-secondary btn-sm" (click)="editMoreFields()">
                {{ 'Edit more fields' | translate }}
              </button>
              <button class="btn btn-primary btn-sm" (click)="onClose()">
                {{ 'Done' | translate }}
              </button>
            }
          }
        </div>
      </mat-dialog-actions>